import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, GitBranch, Users, Receipt, BarChart3, Bell, LogOut, Building2, Menu, X, CalendarX, Banknote, UserCog, Palmtree, Sun, Moon, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import NotificationDrawer from "@/components/NotificationDrawer";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/", label: "Gösterge Paneli", icon: LayoutDashboard, end: true },
  { to: "/faturalar", label: "Faturalar", icon: Receipt },
  { to: "/masraflar", label: "Masraflar", icon: Banknote },
  { to: "/personel", label: "Personel & Maaş", icon: Users },
  { to: "/devamsizlik", label: "Devamsızlık", icon: CalendarX },
  { to: "/yillik-izin", label: "Yıllık İzin", icon: Palmtree },
  { to: "/evraklar", label: "Evraklar", icon: FileText, adminOnly: true },
  { to: "/subeler", label: "Şubeler", icon: GitBranch },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3 },
  { to: "/kullanicilar", label: "Şube Kullanıcıları", icon: UserCog, adminOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [light, setLight] = useState(() => localStorage.getItem("theme") === "light");

  const toggleTheme = () => {
    const next = !light;
    setLight(next);
    localStorage.setItem("theme", next ? "light" : "dark");
    document.documentElement.classList.toggle("light", next);
  };

  const { data: notifs = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 60000,
  });

  const overdueCount = notifs.filter((n) => n.days_left < 0).length;
  const badgeCount = notifs.length;

  const handleLogout = async () => {
    await logout();
    navigate("/giris");
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-6 py-6">
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="font-heading font-extrabold text-xl tracking-tight text-white leading-none">VİLAYET</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Muhasebe Sistemi</div>
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {nav.filter((item) => !item.adminOnly || user?.role !== "sube").map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? "bg-blue-600/15 text-blue-400 border border-blue-600/30"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 pb-5">
        <div className="glass rounded-xl p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center text-sm font-semibold text-white">
            {(user?.name || user?.email || "V")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-white truncate">{user?.name || "Yönetici"}</div>
            <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <button data-testid="logout-button" onClick={handleLogout} className="text-slate-500 hover:text-rose-400 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0B0F17] grain flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-slate-800/70 bg-[#0D1220] fixed inset-y-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-[#0D1220] border-r border-slate-800/70">
            <button className="absolute top-5 right-4 text-slate-400" onClick={() => setMobileOpen(false)}>
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 relative z-10">
        {/* Topbar */}
        <header className="sticky top-0 z-20 glass border-b border-slate-800/70 px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-300" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Hoş geldiniz</div>
              <div className="font-heading font-semibold text-white text-lg leading-none mt-0.5">
                {user?.name || "Yönetici"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              title={light ? "Gece moduna geç" : "Gündüz moduna geç"}
              className="w-11 h-11 rounded-xl glass flex items-center justify-center text-slate-300 hover:text-white transition-colors"
            >
              {light ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>
            <button
              data-testid="notification-bell"
            onClick={() => setNotifOpen(true)}
            className="relative w-11 h-11 rounded-xl glass flex items-center justify-center text-slate-300 hover:text-white transition-colors"
          >
            <Bell className="w-5 h-5" />
            {badgeCount > 0 && (
              <span
                data-testid="notification-bell-badge"
                className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full text-[11px] font-bold flex items-center justify-center text-white ${
                  overdueCount > 0 ? "bg-rose-500" : "bg-amber-500"
                }`}
              >
                {badgeCount}
              </span>
            )}
            </button>
          </div>
        </header>

        <main className="p-4 sm:p-8">
          <Outlet />
        </main>
      </div>

      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)} notifs={notifs} />
    </div>
  );
}
